(function () {
  var BANKING_KEY_STORAGE = 'gigi:banking-key';
  var BANKING_RETRY_COOLDOWN_MS = 1500;
  var DIALOG_CLOSE_MS = 180;
  var COPY_POPOVER_DISMISS_SELECTOR = [
    '.gigi-corporate-details__copy',
    '.gigi-corporate-details__copy-all',
    '.gigi-corporate-details__icon-action',
    '.gigi-corporate-details__swift-info',
    '.gigi-corporate-details__swift-popover-copy'
  ].join(', ');

  function clearButtonCopied(button) {
    window.clearTimeout(button._gigiCopyTimer);
    button.classList.remove('is-copied');

    if (button._gigiCopyDefaultAriaLabel) {
      button.setAttribute('aria-label', button._gigiCopyDefaultAriaLabel);
    }

    if (button.hasAttribute('data-gigi-qr-copy-btn')) {
      button.textContent = 'Copy Image';
      button.disabled = false;
    }
  }

  function clearCopiedButtons(section, activeButton) {
    section.querySelectorAll('[data-corporate-copy].is-copied').forEach(function (button) {
      if (button !== activeButton) clearButtonCopied(button);
    });
  }

  function setButtonCopied(button) {
    if (!button._gigiCopyDefaultAriaLabel) {
      button._gigiCopyDefaultAriaLabel = button.getAttribute('aria-label') || 'Copy';
    }

    var copiedLabel = button.classList.contains('gigi-corporate-details__copy-all')
      ? 'Copied everything!'
      : 'Copied!';

    button.classList.add('is-copied');
    button.setAttribute('aria-label', copiedLabel);

    if (button.hasAttribute('data-gigi-qr-copy-btn')) {
      button.textContent = 'Copied!';
      button.disabled = true;
    }

    window.clearTimeout(button._gigiCopyTimer);
    button._gigiCopyTimer = window.setTimeout(function () {
      clearButtonCopied(button);
    }, 3000);
  }

  function fallbackCopy(value) {
    var input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.top = '-9999px';
    document.body.appendChild(input);
    var copied = false;
    try {
      input.select();
      copied = document.execCommand('copy');
    } catch (error) {
      console.warn('Failed to copy text.');
    } finally {
      document.body.removeChild(input);
    }
    return copied;
  }

  async function copyTextFallback(button, value) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        setButtonCopied(button);
        return;
      } catch (error) {
        // Try the legacy copy path below if clipboard access fails.
      }
    }

    if (fallbackCopy(value)) setButtonCopied(button);
  }

  async function copyValue(button) {
    var value = button.getAttribute('data-corporate-copy') || '';
    if (!value) return;

    if (value.indexOf('.png') !== -1 && navigator.clipboard && navigator.clipboard.write && window.ClipboardItem) {
      button.disabled = true;
      var imageCopied = false;

      try {
        var imageBlob = fetch(value).then(function (res) {
          if (!res.ok) throw new Error('QR image request failed');
          return res.blob();
        });
        // The executor starts the write during the click and captures setup errors.
        // Promise.all observes the image even if ClipboardItem or write throws.
        await Promise.all([
          imageBlob,
          new Promise(function (resolve) {
            var item = new window.ClipboardItem({ 'image/png': imageBlob });
            resolve(navigator.clipboard.write([item]));
          })
        ]);
        imageCopied = true;
      } catch (err) {
        console.error('Failed to copy QR image:', err);
      } finally {
        button.disabled = false;
      }

      if (imageCopied) {
        setButtonCopied(button);
        return;
      }
    }

    await copyTextFallback(button, value);
  }

  function getDialogCloseDelay() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    return DIALOG_CLOSE_MS;
  }

  function openAnimatedDialog(modal) {
    window.clearTimeout(modal._gigiDialogCloseTimer);
    modal.removeAttribute('data-gigi-closing');
    document.body.classList.add('overflow-hidden');

    if (typeof modal.showModal === 'function') {
      if (!modal.open) modal.showModal();
      return;
    }

    modal.setAttribute('open', '');
  }

  function closeAnimatedDialog(modal, afterClose) {
    window.clearTimeout(modal._gigiDialogCloseTimer);

    if (!modal.open && !modal.hasAttribute('open')) {
      document.body.classList.remove('overflow-hidden');
      if (typeof afterClose === 'function') afterClose();
      return;
    }

    modal.setAttribute('data-gigi-closing', 'true');
    modal._gigiDialogCloseTimer = window.setTimeout(function () {
      if (modal.open && typeof modal.close === 'function') {
        modal.close();
      } else {
        modal.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }

      modal.removeAttribute('data-gigi-closing');
      if (typeof afterClose === 'function') afterClose();
    }, getDialogCloseDelay());
  }

  function initQrModal(section) {
    if (section.dataset.qrModalReady === 'true') return;

    var openButton = section.querySelector('[data-gigi-upi-qr-open]');
    var modal = section.querySelector('[data-gigi-upi-qr-modal]');

    if (!openButton || !modal) return;

    section.dataset.qrModalReady = 'true';
    openButton.setAttribute('aria-expanded', 'false');

    function openModal() {
      openButton.setAttribute('aria-expanded', 'true');
      openButton.blur();
      openAnimatedDialog(modal);
    }

    function closeModal() {
      closeAnimatedDialog(modal, function () {
        openButton.setAttribute('aria-expanded', 'false');
        restoreFocusAfterModal(openButton);
      });
    }

    openButton.addEventListener('click', openModal);

    modal.querySelectorAll('[data-gigi-upi-qr-close]').forEach(function (button) {
      button.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', function (event) {
      if (event.target === modal) closeModal();
    });

    modal.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeModal();
    });

    modal.addEventListener('close', function () {
      openButton.setAttribute('aria-expanded', 'false');
      modal.removeAttribute('data-gigi-closing');
      document.body.classList.remove('overflow-hidden');
    });
  }

  function base64ToBytes(value) {
    var binary = window.atob(value);
    var bytes = new Uint8Array(binary.length);
    for (var i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  function bytesToBase64(buffer) {
    var bytes = new Uint8Array(buffer);
    var binary = '';
    for (var i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  function readStoredBankingKey() {
    try {
      return window.sessionStorage.getItem(BANKING_KEY_STORAGE);
    } catch (error) {
      return null;
    }
  }

  function writeStoredBankingKey(value) {
    try {
      window.sessionStorage.setItem(BANKING_KEY_STORAGE, value);
    } catch (error) {
      // Unlocking still works without session persistence.
    }
  }

  function clearStoredBankingKey() {
    try {
      window.sessionStorage.removeItem(BANKING_KEY_STORAGE);
    } catch (error) {
      // Ignore storage failures.
    }
  }

  function buildBankCopy(bank) {
    return [
      'Acct. No.: ' + (bank.account || ''),
      'IFSC: ' + (bank.ifsc || ''),
      'Bank: ' + (bank.bank || ''),
      'SWIFT: ' + (bank.swift || ''),
      'Branch: ' + (bank.branch || '')
    ].join('; ');
  }

  function populateBankCard(card, bank) {
    var kicker = card.querySelector('[data-gigi-bank-kicker]');
    if (kicker) kicker.textContent = bank.bank || '';

    card.querySelectorAll('[data-gigi-bank-field]').forEach(function (valueEl) {
      var value = bank[valueEl.getAttribute('data-gigi-bank-field')] || '';
      valueEl.textContent = value;

      var row = valueEl.closest('.gigi-corporate-details__field');
      var copyButton = row && row.querySelector('.gigi-corporate-details__copy');
      if (copyButton) copyButton.setAttribute('data-corporate-copy', value);
    });

    var copyAll = card.querySelector('[data-gigi-bank-copy-all]');
    if (copyAll) copyAll.setAttribute('data-corporate-copy', buildBankCopy(bank));

    var noteWrap = card.querySelector('[data-gigi-bank-swift-note-wrap]');
    if (!noteWrap) return;

    if (bank.swiftNote) {
      var noteText = noteWrap.querySelector('[data-gigi-bank-swift-note]');
      var noteButton = noteWrap.querySelector('[data-gigi-bank-swift-note-button]');
      var noteCopy = noteWrap.querySelector('[data-gigi-bank-swift-note-copy]');

      if (noteText) noteText.textContent = bank.swiftNote;
      if (noteButton) noteButton.setAttribute('aria-label', bank.swiftNote);
      if (noteCopy && bank.swiftNoteCode) {
        noteCopy.setAttribute('data-corporate-copy', bank.swiftNoteCode);
        noteCopy.setAttribute('aria-label', 'Copy ' + bank.swiftNoteCode);
      }

      noteWrap.hidden = false;
    } else {
      noteWrap.hidden = true;
    }
  }

  function initBankingGate(section) {
    if (section.dataset.bankingReady === 'true') return;

    var cipherEl = section.querySelector('[data-gigi-banking-cipher]');
    var gate = section.querySelector('[data-gigi-banking-gate]');
    var modal = section.querySelector('[data-gigi-banking-modal]');
    var openButton = section.querySelector('[data-gigi-banking-open]');
    var cards = section.querySelector('[data-gigi-banking-cards]');
    var contentEls = section.querySelectorAll('[data-gigi-banking-content]');

    if (!cipherEl || !gate || !modal || !openButton || !cards) return;

    section.dataset.bankingReady = 'true';

    var blob = null;
    try {
      blob = JSON.parse(cipherEl.textContent);
    } catch (error) {
      blob = null;
    }

    var subtle = window.crypto && window.crypto.subtle;
    var form = gate.querySelector('[data-gigi-banking-form]');
    var input = form && form.querySelector('input');
    var submit = gate.querySelector('[data-gigi-banking-submit]');
    var errorEl = gate.querySelector('[data-gigi-banking-error]');

    if (!blob || !subtle || !form || !input || !submit) return;

    function decryptWithKey(key) {
      return subtle
        .decrypt({ name: 'AES-GCM', iv: base64ToBytes(blob.iv) }, key, base64ToBytes(blob.data))
        .then(function (buffer) {
          return JSON.parse(new TextDecoder().decode(buffer));
        });
    }

    function setBusy(busy) {
      submit.disabled = busy;
      submit.textContent = busy ? 'Submitting' : 'Submit';
    }

    function showBankingError() {
      clearStoredBankingKey();
      input.value = '';
      submit.textContent = 'Submit';
      if (errorEl) errorEl.hidden = false;

      // Cooldown before the next attempt; manual guessing stays slow.
      window.setTimeout(function () {
        submit.disabled = false;
        input.focus();
      }, BANKING_RETRY_COOLDOWN_MS);
    }

    function openModal() {
      openAnimatedDialog(modal);

      window.setTimeout(function () {
        input.focus();
      }, 0);
    }

    function closeModal(returnFocus) {
      closeAnimatedDialog(modal, function () {
        if (returnFocus === false) return;
        restoreFocusAfterModal(openButton);
      });
    }

    function showBankingContent() {
      contentEls.forEach(function (contentEl) {
        contentEl.hidden = false;
        contentEl.classList.remove('is-revealed');
        window.requestAnimationFrame(function () {
          contentEl.classList.add('is-revealed');
        });
      });
    }

    function reveal(payload) {
      var cardEls = cards.querySelectorAll('[data-gigi-bank-card]');
      (payload.banks || []).forEach(function (bank, index) {
        if (cardEls[index]) populateBankCard(cardEls[index], bank);
      });

      var focusCardsAfterClose = modal.open;
      input.value = '';
      showBankingContent();
      openButton.remove();
      closeAnimatedDialog(modal, function () {
        if (!focusCardsAfterClose) return;
        try {
          cards.focus({ preventScroll: true });
        } catch (error) {
          cards.focus();
        }
      });
    }

    openButton.addEventListener('click', openModal);

    modal.addEventListener('click', function (event) {
      if (event.target === modal) closeModal();
    });

    modal.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeModal();
    });

    modal.addEventListener('close', function () {
      modal.removeAttribute('data-gigi-closing');
      document.body.classList.remove('overflow-hidden');
    });

    input.addEventListener('input', function () {
      if (errorEl) errorEl.hidden = true;
    });

    var storedKey = readStoredBankingKey();
    if (storedKey) {
      subtle
        .importKey('raw', base64ToBytes(storedKey), { name: 'AES-GCM' }, false, ['decrypt'])
        .then(decryptWithKey)
        .then(reveal)
        .catch(function () {
          clearStoredBankingKey();
        });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      var passphrase = input.value.trim();
      if (!passphrase || submit.disabled) return;

      setBusy(true);
      if (errorEl) errorEl.hidden = true;

      subtle
        .importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey'])
        .then(function (baseKey) {
          return subtle.deriveKey(
            { name: 'PBKDF2', salt: base64ToBytes(blob.salt), iterations: blob.iterations, hash: 'SHA-256' },
            baseKey,
            { name: 'AES-GCM', length: 256 },
            true,
            ['decrypt']
          );
        })
        .then(function (key) {
          return decryptWithKey(key).then(function (payload) {
            return subtle.exportKey('raw', key).then(function (rawKey) {
              writeStoredBankingKey(bytesToBase64(rawKey));
              return payload;
            });
          });
        })
        .then(function (payload) {
          setBusy(false);
          reveal(payload);
        })
        .catch(function () {
          showBankingError();
        });
    });
  }

  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-gigi-corporate-details]').forEach(function (section) {
      if (section.dataset.copyReady !== 'true') {
        section.dataset.copyReady = 'true';
        section.addEventListener('click', function (event) {
          var button = event.target.closest('[data-corporate-copy]');
          if (!button || !section.contains(button)) return;
          copyValue(button);
        });

        var clearCopiedOnActionEnter = function (event) {
          var action = event.target.closest(COPY_POPOVER_DISMISS_SELECTOR);
          if (!action || !section.contains(action)) return;
          clearCopiedButtons(section, action);
        };

        section.addEventListener('pointerover', clearCopiedOnActionEnter);
        section.addEventListener('focusin', clearCopiedOnActionEnter);
      }

      initQrModal(section);
      initBankingGate(section);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    init(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
